const wecIDKey = 'molepool:wec:id';
const validWECID = value => typeof value === 'string' && /^(?:[1-9A-HJ-NP-Za-km-z]{12}|[0-9a-f]{32})$/.test(value);

// Keep only the generated WEC ID; payout addresses never enter browser storage.
export function savedWECID(storage) {
  try {
    const id = (storage || globalThis.localStorage)?.getItem(wecIDKey);
    return validWECID(id) ? id : '';
  } catch { return ''; }
}

export function saveWECID(id, storage) {
  if (!validWECID(id)) return false;
  try { (storage || globalThis.localStorage).setItem(wecIDKey, id); return true; }
  catch { return false; }
}

export function forgetWECID(storage) {
  try { (storage || globalThis.localStorage)?.removeItem(wecIDKey); }
  catch { /* Storage may be unavailable. */ }
}

export function miningPassword(id = '', difficulty) {
  if (id && !validWECID(id)) return '';
  const tokens = id ? [`aux=${id}`] : [];
  if (difficulty !== undefined) {
    const value = String(difficulty).trim();
    if (!/^[0-9]+(?:\.[0-9]{1,8})?$/.test(value) || !Number.isFinite(Number(value)) || Number(value) <= 0) return '';
    tokens.push(`d=${value}`);
  }
  return tokens.join(';') || 'x';
}

export async function registerWEC(base, address, signal) {
  let response;
  try {
    response = await fetch(`${base.replace(/\/$/, '')}/zec/aux/register`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ address }), cache: 'no-store', credentials: 'same-origin', signal,
    });
  } catch (error) {
    if (error.name === 'AbortError') throw error;
    throw new Error('aux_unavailable');
  }
  const reply = await response.json().catch(() => null);
  if (!response.ok || reply?.status !== true) {
    const allowed = ['invalid_address', 'invalid_request', 'too_many_attempts', 'aux_unavailable'];
    const error = new Error(response.status === 429 ? 'too_many_attempts' : allowed.includes(reply?.error) ? reply.error : 'aux_unavailable');
    if (response.status === 429) {
      const seconds = Number(response.headers.get('Retry-After'));
      error.retryAfter = Number.isFinite(seconds) && seconds > 0 ? Math.min(Math.ceil(seconds), 3600) : 60;
    }
    throw error;
  }
  if (!validWECID(reply.result?.id) || reply.result?.coin !== 'WEC') throw new Error('aux_unavailable');
  return { id: reply.result.id, coin: 'WEC' };
}

export function formatZat(value) {
  if (typeof value !== 'string' || !/^\d+$/.test(value)) return '—';
  const amount = BigInt(value);
  return `${amount / 100000000n}.${String(amount % 100000000n).padStart(8, '0')}`;
}

export function blockConfirmationPercent(item, depth) {
  if (item.status !== 'immature' || item.orphan || !Number.isInteger(item.confirm) || item.confirm < 0 || !Number.isInteger(depth) || depth < 1) return null;
  return Math.min(100, item.confirm * 100 / depth);
}

export function publicAuxResult(result, kind) {
  if (!result || !Array.isArray(result.items) || !Number.isSafeInteger(result.total) || result.total < 0) throw new Error('aux_unavailable');
  const fields = kind === 'payments'
    ? ['miner', 'identityType', 'coin', 'time', 'amountZat', 'status', 'txid']
    : ['miner', 'identityType', 'coin', 'hash', 'height', 'worker', 'time', 'shareDifficulty', 'networkDifficulty', 'rewardZat', 'creditZat', 'confirm', 'orphan', 'status'];
  const items = result.items.map(row => {
    const item = Object.fromEntries(fields.map(field => [field, row[field]]));
    if (kind === 'rewards' && row.coin === 'WEC' && validWECID(row.payoutId)) item.payoutId = row.payoutId;
    return item;
  });
  const balances = result.balances ? Object.fromEntries(['coin', 'immatureZat', 'balanceZat', 'pendingZat', 'paidZat'].map(field => [field, result.balances[field]])) : null;
  return { items, total: result.total, balances };
}

export async function auxRequest(base, path, { body, signal, pvc = '' } = {}) {
  let response;
  try { response = await fetch(`${base.replace(/\/$/, '')}/${path}`, {
    method: body ? 'POST' : 'GET', cache: 'no-store', credentials: 'same-origin', signal,
    headers: { ...(body ? { 'Content-Type': 'application/json' } : {}), ...(pvc ? { 'X-Molepool-PVC': pvc } : {}) },
    ...(body ? { body: JSON.stringify(body) } : {}),
  }); } catch (error) {
    if (error.name === 'AbortError') throw error;
    throw new Error('aux_unavailable');
  }
  const reply = await response.json().catch(() => null);
  if (!response.ok || !reply?.status) {
    const accessErrors = { 401: 'invalid_pvc', 403: 'private_profile', 404: 'profile_not_found', 429: 'too_many_attempts' };
    if (accessErrors[response.status]) {
      const error = new Error(accessErrors[response.status]);
      error.status = response.status;
      if (response.status === 429) error.retryAfter = Math.min(Math.max(Number(response.headers.get('Retry-After')) || 60, 1), 3600);
      throw error;
    }
    const allowed = ['invalid_request', 'unsupported_coin'];
    throw new Error(allowed.includes(reply?.error) ? reply.error : 'aux_unavailable');
  }
  return reply.result;
}
